import { call, put, takeLatest } from 'redux-saga/effects';

import { monolithApiUrl } from '../../config/app';
import { endpoints, queryParams } from '../../constants/routing';
import {
  IAddCampaignRequest,
  IAddCampaignResponse,
  IDeleteCampaignRequest,
  IDeleteCampaignResponse,
  IEditCampaignRequest,
  IEditCampaignResponse,
  IFetchCampaignsRequest,
  IFetchCampaignsResponse,
  IFetchCampaignSummariesRequestPayload,
  IFetchCampaignSummariesResponsePayload,
} from '../../types/campaigns';
import { IResponse } from '../../types/http';
import { IPromiseCallbacks, IReduxAction } from '../../types/redux';
import { IApiError, IDownloadCSVSuccessPayload, SupportedFileTypesEnum } from '../../types/shell';
import { mapCampaignItemsForEditCampaignRequest } from '../../utils/campaigns';
import { buildEndpointRoute } from '../../utils/routing';
import * as CampaignsActions from '../actions/campaigns';
import { AuthRequest } from './helpers';

export function* campaignsWorkerSaga(action: IReduxAction<IFetchCampaignsRequest>) {
  try {
    const endpoint = `${monolithApiUrl}${endpoints.getCampaigns}`;

    const qs = {
      ...(action.payload?.page ? { page: action.payload.page } : {}),
      ...(action.payload?.page_size ? { page_size: action.payload.page_size } : {}),
      ...(action.payload?.search_query ? { search_query: action.payload.search_query } : {}),
      ...(action.payload?.department_id ? { department_id: action.payload.department_id } : {}),
      ...(action.payload?.fulfillment_center_id ? { fulfillment_center_id: action.payload.fulfillment_center_id } : {}),
    };

    const response: IResponse<IFetchCampaignsResponse | IApiError> = yield call<any>(AuthRequest, {
      queryParams: qs,
      method: 'GET',
      endpoint,
    });

    if (!response.ok) {
      yield put(CampaignsActions.fetchCampaignsFailure(response.body as IApiError));
    } else {
      yield put(CampaignsActions.fetchCampaignsSuccess(response.body as IFetchCampaignsResponse));
    }
  } catch (error) {
    yield put(CampaignsActions.fetchCampaignsFailure(error));
  }
}

export function* addCampaignWorkerSaga(action: IReduxAction<IAddCampaignRequest>) {
  const { campaign, resolve, reject } = action.payload || {};
  const { ds_header_color, ds_button_color, ds_shipping_page_title, ds_shipping_page_body, ...campaignData } =
    campaign || {};

  try {
    const endpoint = `${monolithApiUrl}${endpoints.addCampaign}`;
    const response: IResponse<IAddCampaignResponse | IApiError> = yield call<any>(AuthRequest, {
      endpoint,
      method: 'POST',
      body: JSON.stringify({
        campaign: {
          ...campaignData,
          org_id: undefined,
          items: campaign?.items?.map((item) => ({
            quantity: item.quantity,
            item_id: item.item_id,
            is_hidden_for_recipient: item.is_hidden_for_recipient,
          })),
          ...(ds_header_color ? { ds_header_color } : {}),
          ...(ds_button_color ? { ds_button_color } : {}),
          ...(ds_shipping_page_body ? { ds_shipping_page_body } : {}),
          ...(ds_shipping_page_title ? { ds_shipping_page_title } : {}),
        },
      }),
    });

    if (!response.ok) {
      if (typeof reject === 'function') {
        reject(response.body);
      }
      yield put(CampaignsActions.addCampaignFailure(response.body as IApiError));
    } else {
      if (typeof resolve === 'function') {
        resolve(response.body);
      }
      yield put(CampaignsActions.addCampaignSuccess(response.body as IAddCampaignResponse));
    }
  } catch (error) {
    if (typeof reject === 'function') {
      reject(error);
    }
    yield put(CampaignsActions.addCampaignFailure(error));
  }
}

export function* editCampaignWorkerSaga(action: IReduxAction<IEditCampaignRequest>) {
  const { campaign, resolve, reject } = action.payload || {};
  const { ds_header_color, ds_button_color, ds_shipping_page_title, ds_shipping_page_body, ...campaignData } =
    campaign || {};

  try {
    if (!campaign?.box_id) {
      throw new Error('Campaign ID is required');
    }

    const endpoint = `${monolithApiUrl}${buildEndpointRoute(endpoints.editCampaign, { id: campaign?.box_id })}`;
    const response: IResponse<IEditCampaignResponse | IApiError> = yield call<any>(AuthRequest, {
      endpoint,
      method: 'PATCH',
      body: JSON.stringify({
        campaign: {
          ...campaignData,
          uid: campaign?.box_id,
          org_id: undefined,
          items: campaign?.items?.map(mapCampaignItemsForEditCampaignRequest),
          ...(ds_header_color ? { ds_header_color } : {}),
          ...(ds_button_color ? { ds_button_color } : {}),
          ...(ds_shipping_page_body ? { ds_shipping_page_body } : {}),
          ...(ds_shipping_page_title ? { ds_shipping_page_title } : {}),
        },
      }),
    });

    if (!response.ok) {
      if (typeof reject === 'function') {
        reject(response.body);
      }
      yield put(CampaignsActions.editCampaignFailure(response.body as IApiError));
    } else {
      if (typeof resolve === 'function') {
        resolve(response.body);
      }
      yield put(CampaignsActions.editCampaignSuccess(response.body as IEditCampaignResponse));
    }
  } catch (error) {
    if (typeof reject === 'function') {
      reject(error);
    }
    yield put(CampaignsActions.editCampaignFailure(error));
  }
}

export function* deleteCampaignWorkerSaga(action: IReduxAction<IDeleteCampaignRequest>) {
  const { uid, resolve, reject } = action.payload || {};
  try {
    if (!uid) {
      throw new Error('Campaign ID is required');
    }

    const endpoint = `${monolithApiUrl}${buildEndpointRoute(endpoints.deleteCampaign, { id: uid })}`;

    const response: IResponse<IDeleteCampaignResponse | IApiError> = yield call<any>(AuthRequest, {
      endpoint,
      method: 'DELETE',
    });

    if (!response.ok) {
      if (typeof reject === 'function') {
        reject(response.body);
      }
      yield put(CampaignsActions.deleteCampaignFailure(response.body as IApiError));
    } else {
      if (typeof resolve === 'function') {
        resolve(response.body);
      }
      yield put(CampaignsActions.deleteCampaignSuccess({ uid } as IDeleteCampaignResponse));
    }
  } catch (error) {
    if (typeof reject === 'function') {
      reject(error);
    }
    yield put(CampaignsActions.deleteCampaignFailure(error));
  }
}

export function* fetchCampaignSummariesWorkerSaga(action: IReduxAction<IFetchCampaignSummariesRequestPayload>) {
  try {
    const { campaign_ids } = action.payload!;
    const endpoint = `${monolithApiUrl}${endpoints.getCampaignSummaries}`;

    const response: IResponse<IFetchCampaignSummariesResponsePayload | IApiError> = yield call<any>(AuthRequest, {
      endpoint,
      method: 'POST',
      body: JSON.stringify({ campaign_ids }),
    });

    if (!response.ok) {
      yield put(CampaignsActions.fetchCampaignSummariesFailure(response.body as IApiError));
    } else {
      yield put(
        CampaignsActions.fetchCampaignSummariesSuccess(response.body as IFetchCampaignSummariesResponsePayload),
      );
    }
  } catch (error) {
    yield put(CampaignsActions.fetchCampaignSummariesFailure(error));
  }
}

export function* downloadCampaignRequestWorkerSaga(action: IReduxAction<IPromiseCallbacks>) {
  const { resolve, reject } = action.payload || {};

  try {
    const endpoint = `${monolithApiUrl}${endpoints.downloadCampaignReportCSV}`;
    const qs = { [queryParams.fileType]: SupportedFileTypesEnum.CSV };

    const response: IResponse<IDownloadCSVSuccessPayload | IApiError> = yield call(AuthRequest, {
      endpoint,
      queryParams: qs,
    });

    if (response.ok) {
      yield put(CampaignsActions.downloadCampaignReportCSVSuccess(response.body as IDownloadCSVSuccessPayload));
      resolve?.(response.body);
    } else {
      yield put(CampaignsActions.downloadCampaignReportCSVFailure(response.body as IApiError));
      reject?.(response.body);
    }
  } catch (e) {
    yield put(CampaignsActions.downloadCampaignReportCSVFailure(e));
    reject?.(e);
  }
}

const sagas = {
  *watchCampaignsRequest() {
    yield takeLatest(CampaignsActions.FETCH_CAMPAIGNS_REQUEST, campaignsWorkerSaga);
  },
  *watchAddCampaignRequest() {
    yield takeLatest(CampaignsActions.ADD_CAMPAIGN_REQUEST, addCampaignWorkerSaga);
  },
  *watchEditCampaignRequest() {
    yield takeLatest(CampaignsActions.EDIT_CAMPAIGN_REQUEST, editCampaignWorkerSaga);
  },
  *watchFetchCampaignSummariesRequest() {
    yield takeLatest(CampaignsActions.FETCH_CAMPAIGN_SUMMARIES_REQUEST, fetchCampaignSummariesWorkerSaga);
  },
  *watchDeleteCampaignRequest() {
    yield takeLatest(CampaignsActions.DELETE_CAMPAIGN_REQUEST, deleteCampaignWorkerSaga);
  },
  *watchDownloadCampaignReportCSVRequest() {
    yield takeLatest(CampaignsActions.DOWNLOAD_CAMPAIGN_REPORT_CSV_REQUEST, downloadCampaignRequestWorkerSaga);
  },
};

export default sagas;
